import React from "react";
import { useQuery } from "react-query";
import axios from "axios";
import Image from "next/image";
import Link from "next/link";
import millify from "millify";
import { cn } from "@jammable/ui-core";

interface TopCreator {
  userId: string;
  username: string;
  avatar: string;
  verifiedAccount: boolean;
  audienceCount: number;
  modelCount: number;
}

const TopCreators = () => {
  const { data, isLoading, error } = useQuery(
    "top-creators",
    async () => {
      const { data } = await axios.get("/api/user/getTopCreators");
      return data;
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  if (isLoading) {
    return (
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-6 tw-gap-3 tw-w-full">
        {[...Array(12)].map((_, idx) => (
          <div
            key={idx}
            className="tw-animate-pulse tw-flex tw-flex-col tw-items-center tw-bg-gray-50 tw-p-4 tw-rounded-3xl"
          >
            <div className="tw-bg-gray-200 tw-rounded-full tw-w-16 tw-h-16 tw-mb-3"></div>
            <div className="tw-bg-gray-200 tw-h-4 tw-w-20 tw-rounded-full tw-mb-1"></div>
            <div className="tw-bg-gray-200 tw-h-3 tw-w-24 tw-rounded-full"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error || !data?.topCreators?.length) {
    return null;
  }

  return (
    <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-6 tw-gap-3 tw-w-full">
      {data.topCreators.map((creator: TopCreator, index: number) => (
        <Link
          href={`/@${creator.username}`}
          key={creator.userId}
          className="tw-flex tw-flex-col tw-items-center tw-bg-gray-50 hover:tw-bg-gray-100 tw-transition-colors tw-p-4 tw-rounded-4xl tw-cursor-pointer tw-group"
        >
          <div className="tw-relative">
            <Image
              src={creator.avatar}
              alt={creator.username}
              width={80}
              height={80}
              className={cn(
                "tw-rounded-full tw-border-4",
                index === 0
                  ? "tw-border-yellow-400"
                  : index === 1
                    ? "tw-border-slate-400"
                    : index === 2
                      ? "tw-border-amber-700"
                      : "tw-border-gray-200",
              )}
            />
            <div className="tw-absolute tw-bottom-0 tw-right-0 tw-bg-white tw-rounded-full tw-p-1  tw-w-size-6 tw-flex tw-items-center tw-justify-center">
              <div
                className={cn(
                  "tw-rounded-full tw-text-[10px] tw-font-black tw-flex tw-items-center tw-justify-center tw-text-white tw-w-5 tw-h-5",
                  index === 0
                    ? "tw-bg-yellow-400"
                    : index === 1
                      ? "tw-bg-slate-400"
                      : index === 2
                        ? "tw-bg-amber-700"
                        : "tw-bg-gray-500",
                )}
              >
                {index + 1}
              </div>
            </div>
          </div>

          <div>
            <div className="tw-font-bold tw-mt-2 tw-text-center tw-group-hover:tw-text-purple-700 tw-transition-colors">
              @{creator.username}
            </div>

            <div>
              <div className="tw-flex tw-items-center tw-gap-1 tw-text-black tw-text-sm tw-justify-center">
                <span className="tw-text-xl tw-font-black tw-text-bold">
                  {millify(creator.audienceCount)}
                </span>
              </div>
              <span className="tw-text-xs tw-uppercase tw-font-bold tw-text-black/40 tw-flex tw-items-center tw-gap-1 tw-text-center tw-justify-center tw-tracking-widest">
                audience size
              </span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default TopCreators;
